import React, { useState, useEffect } from 'react';
import './AdminCVTemplates.scss';

interface CVTemplate {
  id: number;
  category: string;
  value: string;
  label?: string; // Optional - falls back to value if not present
  image_url?: string; // Optional image for the template
  created_at: string;
}

interface NewTemplate {
  category: string;
  value: string;
  label: string;
}

const AdminCVTemplates: React.FC = () => {
  const [templates, setTemplates] = useState<CVTemplate[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [selectedCategory, setSelectedCategory] = useState<string>('education_level');
  const [isAddingNew, setIsAddingNew] = useState(false);
  const [isBulkImport, setIsBulkImport] = useState(false);
  const [bulkImportText, setBulkImportText] = useState('');
  const [bulkImportProgress, setBulkImportProgress] = useState<string | null>(null);
  const [editingTemplate, setEditingTemplate] = useState<CVTemplate | null>(null);
  const [editForm, setEditForm] = useState<{ value: string; label: string }>({ value: '', label: '' });
  const [uploadingImage, setUploadingImage] = useState<number | null>(null);
  const [newTemplate, setNewTemplate] = useState<NewTemplate>({
    category: 'education_level',
    value: '',
    label: ''
  });

  const categories = [
    { value: 'education_level', label: 'Išsilavinimo lygiai' },
    { value: 'degree', label: 'Laipsnis' },
    { value: 'specialization', label: 'Specialybė' },
    { value: 'field_of_study', label: 'Studijų sritys' },
    { value: 'work_location', label: 'Darbo vietos' },
    { value: 'work_field', label: 'Darbo sritys' },
    { value: 'work_type', label: 'Darbo tipai' },
    { value: 'work_time', label: 'Darbo laikai' },
    { value: 'language', label: 'Kalbos' },
    { value: 'position', label: 'Pareigos' },
    { value: 'institution', label: 'Mokymo įstaigos' },
    { value: 'city', label: 'Miestai' },
    { value: 'computer_skill_office', label: 'Biuro programos' },
    { value: 'computer_skill_accounting', label: 'Apskaitos programos' },
    { value: 'computer_skill_os', label: 'Operacinės sistemos' },
    { value: 'computer_skill_programming', label: 'Programavimo kalbos' },
    { value: 'computer_skill_database', label: 'Duomenų bazės' },
    { value: 'proficiency_level', label: 'Įgūdžių lygiai' },
    { value: 'driving_category', label: 'Vairavimo kategorijos' },
    { value: 'cv_language', label: 'CV kalbos' },
    { value: 'job_language', label: 'Skelbimo kalbos' },
    { value: 'company_activity', label: 'Įmonės veiklos sritys' },
    { value: 'position_type', label: 'Pareigybių tipai' },
    { value: 'start_date_type', label: 'Darbo pradžios tipai' },
    { value: 'gender', label: 'Lytis' }
  ];

  useEffect(() => {
    fetchTemplates();
  }, []);

  const fetchTemplates = async () => {
    try {
      setLoading(true);
      const token = localStorage.getItem('admin_token');
      const API_URL = '/api';
      const response = await fetch(`${API_URL}/cv-templates`, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });

      if (response.ok) {
        const result = await response.json();
        console.log('CV Templates API Response:', result);
        const templatesData = result.data || result;
        // Ensure we always have an array
        setTemplates(Array.isArray(templatesData) ? templatesData : []);
      } else {
        const errorText = await response.text();
        console.error('CV Templates API Error:', response.status, errorText);
        setError('Nepavyko užkrauti šablonų');
      }
    } catch (error) {
      console.error('Error fetching templates:', error);
      setError('Klaida kraunant šablonų duomenis');
    } finally {
      setLoading(false);
    }
  };

  const handleAddTemplate = async () => {
    if (!newTemplate.value.trim()) {
      setError('Visi laukai yra privalomi');
      return;
    }

    try {
      const token = localStorage.getItem('admin_token');
      const API_URL = '/api';
      const response = await fetch(`${API_URL}/cv-templates`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(newTemplate)
      });

      if (response.ok) {
        const addedTemplate = await response.json();
        setTemplates([...templates, addedTemplate]);
        setNewTemplate({
          category: selectedCategory,
          value: '',
          label: ''
        });
        setIsAddingNew(false);
        setError(null);
      } else {
        setError('Nepavyko pridėti šablono');
      }
    } catch (error) {
      console.error('Error adding template:', error);
      setError('Klaida pridedant šabloną');
    }
  };

  const handleStartEdit = (template: CVTemplate) => {
    setEditingTemplate(template);
    setEditForm({ value: template.value, label: template.label || template.value });
  };

  const handleCancelEdit = () => {
    setEditingTemplate(null);
    setEditForm({ value: '', label: '' });
  };

  const handleSaveEdit = async () => {
    if (!editingTemplate) return;
    
    if (!editForm.value.trim()) {
      setError('Reikšmė yra privaloma');
      return;
    }

    try {
      const token = localStorage.getItem('admin_token');
      const API_URL = '/api';
      const response = await fetch(`${API_URL}/cv-templates/${editingTemplate.id}`, {
        method: 'PUT',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          value: editForm.value
        })
      });

      if (response.ok) {
        // Update local state
        setTemplates(templates.map(t => 
          t.id === editingTemplate.id 
            ? { ...t, value: editForm.value }
            : t
        ));
        setEditingTemplate(null);
        setEditForm({ value: '', label: '' });
        setError(null);
      } else {
        setError('Nepavyko atnaujinti šablono');
      }
    } catch (error) {
      console.error('Error updating template:', error);
      setError('Klaida atnaujinant šabloną');
    }
  };

  const handleDeleteTemplate = async (templateId: number) => {
    if (!window.confirm('Ar tikrai norite ištrinti šį šabloną?')) {
      return;
    }

    try {
      const token = localStorage.getItem('admin_token');
      const API_URL = '/api';
      const response = await fetch(`${API_URL}/cv-templates/${templateId}`, {
        method: 'DELETE',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });

      if (response.ok) {
        setTemplates(templates.filter(template => template.id !== templateId));
        setError(null);
      } else {
        setError('Nepavyko ištrinti šablono');
      }
    } catch (error) {
      console.error('Error deleting template:', error);
      setError('Klaida trinant šabloną');
    }
  };

  const handleImageUpload = async (templateId: number, file: File) => {
    try {
      setUploadingImage(templateId);
      const token = localStorage.getItem('admin_token');
      const API_URL = '/api';
      
      const formData = new FormData();
      formData.append('image', file);

      const response = await fetch(`${API_URL}/cv-templates/${templateId}/image`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`
        },
        body: formData
      });

      if (response.ok) {
        const result = await response.json();
        // Update local state with new image URL
        setTemplates(templates.map(t => 
          t.id === templateId 
            ? { ...t, image_url: result.data.image_url }
            : t
        ));
        setError(null);
      } else {
        setError('Nepavyko įkelti nuotraukos');
      }
    } catch (error) {
      console.error('Error uploading image:', error);
      setError('Klaida įkeliant nuotrauką');
    } finally {
      setUploadingImage(null);
    }
  };

  const handleDeleteImage = async (templateId: number) => {
    if (!window.confirm('Ar tikrai norite ištrinti nuotrauką?')) {
      return;
    }

    try {
      const token = localStorage.getItem('admin_token');
      const API_URL = '/api';
      
      const response = await fetch(`${API_URL}/cv-templates/${templateId}/image`, {
        method: 'DELETE',
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });

      if (response.ok) {
        // Update local state to remove image URL
        setTemplates(templates.map(t => 
          t.id === templateId 
            ? { ...t, image_url: undefined }
            : t
        ));
        setError(null);
      } else {
        setError('Nepavyko ištrinti nuotraukos');
      }
    } catch (error) {
      console.error('Error deleting image:', error);
      setError('Klaida trinant nuotrauką');
    }
  };

  const handleBulkImport = async () => {
    if (!bulkImportText.trim()) {
      setError('Įveskite reikšmes importavimui');
      return;
    }

    // Parse the input - each line is a new item
    // Format: "label" or "value,label" per line
    const lines = bulkImportText.split('\n').filter(line => line.trim());
    const itemsToImport: NewTemplate[] = [];

    for (const line of lines) {
      const trimmedLine = line.trim();
      if (!trimmedLine) continue;

      // Check if line contains comma (value,label format)
      if (trimmedLine.includes(',')) {
        const parts = trimmedLine.split(',').map(p => p.trim());
        if (parts.length >= 2) {
          itemsToImport.push({
            category: selectedCategory,
            value: parts[0],
            label: parts[1]
          });
        }
      } else {
        // Just label - auto-generate value from label
        const label = trimmedLine;
        const value = label.toLowerCase()
          .replace(/[ąčęėįšųūž]/g, c => ({
            'ą': 'a', 'č': 'c', 'ę': 'e', 'ė': 'e', 'į': 'i',
            'š': 's', 'ų': 'u', 'ū': 'u', 'ž': 'z'
          }[c] || c))
          .replace(/\s+/g, '_')
          .replace(/[^a-z0-9_]/g, '');
        
        itemsToImport.push({
          category: selectedCategory,
          value: value,
          label: label
        });
      }
    }

    if (itemsToImport.length === 0) {
      setError('Nerasta tinkamų reikšmių importavimui');
      return;
    }

    try {
      setLoading(true);
      setBulkImportProgress(`Importuojama 0/${itemsToImport.length}...`);
      const token = localStorage.getItem('admin_token');
      const API_URL = '/api';
      
      let successCount = 0;
      let errorCount = 0;

      for (let i = 0; i < itemsToImport.length; i++) {
        const item = itemsToImport[i];
        setBulkImportProgress(`Importuojama ${i + 1}/${itemsToImport.length}...`);
        
        try {
          const response = await fetch(`${API_URL}/cv-templates`, {
            method: 'POST',
            headers: {
              'Authorization': `Bearer ${token}`,
              'Content-Type': 'application/json'
            },
            body: JSON.stringify(item)
          });

          if (response.ok) {
            successCount++;
          } else {
            errorCount++;
            console.error(`Failed to import: ${item.label}`);
          }
        } catch (e) {
          errorCount++;
          console.error(`Error importing: ${item.label}`, e);
        }
      }

      setBulkImportProgress(null);
      setIsBulkImport(false);
      setBulkImportText('');
      
      if (errorCount > 0) {
        setError(`Importuota ${successCount} iš ${itemsToImport.length}. Klaidos: ${errorCount}`);
      } else {
        setError(null);
      }
      
      // Reload templates
      await fetchTemplates();
    } catch (error) {
      console.error('Error during bulk import:', error);
      setError('Klaida importuojant duomenis');
      setBulkImportProgress(null);
    } finally {
      setLoading(false);
    }
  };

  const handleSeedPositions = async () => {
    if (!window.confirm('Ar tikrai norite importuoti pareigybių duomenis? Esami darbo sričių, pareigybių tipų ir pareigybių įrašai bus deaktyvuoti ir pakeisti naujais.')) {
      return;
    }

    try {
      setLoading(true);
      const token = localStorage.getItem('admin_token');
      const API_URL = '/api';
      const response = await fetch(`${API_URL}/cv-templates/seed-positions`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });

      if (response.ok) {
        const result = await response.json();
        setError(null);
        alert(`Sėkmingai importuota: ${result.workFields} darbo sričių, ${result.positionTypes} pareigybių tipų, ${result.positions} pareigybių`);
        await fetchTemplates();
      } else {
        const errorText = await response.text();
        console.error('Seed Positions API Error:', response.status, errorText);
        setError('Nepavyko importuoti pareigybių duomenų');
      }
    } catch (error) {
      console.error('Error seeding positions:', error);
      setError('Klaida importuojant pareigybių duomenis');
    } finally {
      setLoading(false);
    }
  };

  const handleSeedTemplates = async () => {
    if (!window.confirm('Ar tikrai norite įkelti pradinius duomenis? Tai pridės visus standartinės CV formos variantus.')) {
      return;
    }

    try {
      setLoading(true);
      const token = localStorage.getItem('admin_token');
      const API_URL = '/api';
      const response = await fetch(`${API_URL}/cv-templates/seed`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });

      if (response.ok) {
        setError(null);
        await fetchTemplates();
      } else {
        const errorText = await response.text();
        console.error('Seed Templates API Error:', response.status, errorText);
        setError('Nepavyko įkelti pradinių duomenų');
      }
    } catch (error) {
      console.error('Error seeding templates:', error);
      setError('Klaida įkeliant pradinius duomenis');
    } finally {
      setLoading(false);
    }
  };

  const filteredTemplates = Array.isArray(templates) ? templates.filter(template => template.category === selectedCategory) : [];

  const getCategoryLabel = (category: string) => {
    const cat = categories.find(c => c.value === category);
    return cat ? cat.label : category;
  };

  if (loading) {
    return (
      <div className="admin-cv-templates">
        <div className="loading">Kraunama...</div>
      </div>
    );
  }

  return (
    <div className="admin-cv-templates">
      <div className="page-header">
        <h1>CV šablonų valdymas</h1>
        <p>Valdykite CV formos pasirinkimo variantus</p>
      </div>

      {error && (
        <div className="error-message">
          {error}
        </div>
      )}

      <div className="templates-container">
        <div className="category-sidebar">
          <h3>Kategorijos</h3>
          <div className="category-list">
            {categories.map((category) => (
              <button
                key={category.value}
                onClick={() => {
                  setSelectedCategory(category.value);
                  setNewTemplate(prev => ({ ...prev, category: category.value }));
                }}
                className={`category-btn ${selectedCategory === category.value ? 'active' : ''}`}
              >
                {category.label}
                <span className="count">
                  {Array.isArray(templates) ? templates.filter(t => t.category === category.value).length : 0}
                </span>
              </button>
            ))}
          </div>
        </div>

        <div className="templates-content">
          <div className="content-header">
            <h2>{getCategoryLabel(selectedCategory)}</h2>
            <div className="header-actions">
              {templates.length === 0 && (
                <button
                  onClick={handleSeedTemplates}
                  className="btn btn-success"
                  disabled={loading}
                >
                  🌱 Įkelti pradinius duomenis
                </button>
              )}
              <button
                onClick={handleSeedPositions}
                className="btn btn-info"
                disabled={loading}
              >
                📂 Importuoti pareigybes
              </button>
              <button
                onClick={() => setIsBulkImport(true)}
                className="btn btn-secondary"
              >
                📋 Masinis importas
              </button>
              <button
                onClick={() => setIsAddingNew(true)}
                className="btn btn-primary"
              >
                + Pridėti naują
              </button>
            </div>
          </div>

          {isBulkImport && (
            <div className="add-template-form bulk-import-form">
              <h3>📋 Masinis importas - {getCategoryLabel(selectedCategory)}</h3>
              <div className="bulk-import-help">
                <p><strong>Formatai:</strong></p>
                <ul>
                  <li>Kiekviena eilutė = naujas įrašas</li>
                  <li><code>Pavadinimas</code> - automatiškai sukuriama reikšmė</li>
                  <li><code>reiksme,Pavadinimas</code> - nurodoma ir reikšmė, ir pavadinimas</li>
                </ul>
                <p><strong>Pavyzdys:</strong></p>
                <pre>Vilnius{'\n'}Kaunas{'\n'}Klaipėda</pre>
                <p>arba</p>
                <pre>vilnius,Vilnius{'\n'}kaunas,Kaunas{'\n'}klaipeda,Klaipėda</pre>
              </div>
              <div className="form-group">
                <label>Įveskite reikšmes (po vieną eilutėje):</label>
                <textarea
                  value={bulkImportText}
                  onChange={(e) => setBulkImportText(e.target.value)}
                  placeholder="Vilnius&#10;Kaunas&#10;Klaipėda&#10;..."
                  rows={10}
                  className="bulk-import-textarea"
                />
              </div>
              {bulkImportProgress && (
                <div className="import-progress">
                  {bulkImportProgress}
                </div>
              )}
              <div className="form-actions">
                <button 
                  onClick={handleBulkImport} 
                  className="btn btn-success"
                  disabled={loading || !bulkImportText.trim()}
                >
                  {loading ? 'Importuojama...' : `Importuoti (${bulkImportText.split('\n').filter(l => l.trim()).length} eilučių)`}
                </button>
                <button 
                  onClick={() => {
                    setIsBulkImport(false);
                    setBulkImportText('');
                    setError(null);
                  }}
                  className="btn btn-secondary"
                  disabled={loading}
                >
                  Atšaukti
                </button>
              </div>
            </div>
          )}

          {isAddingNew && (
            <div className="add-template-form">
              <h3>Pridėti naują šabloną</h3>
              <div className="form-row">
                <div className="form-group">
                  <label>Reikšmė:</label>
                  <input
                    type="text"
                    value={newTemplate.value}
                    onChange={(e) => setNewTemplate(prev => ({ ...prev, value: e.target.value }))}
                    placeholder="pvz. Bakalauras, Vilnius, IT..."
                  />
                </div>
              </div>
              <div className="form-actions">
                <button onClick={handleAddTemplate} className="btn btn-success">
                  Išsaugoti
                </button>
                <button 
                  onClick={() => {
                    setIsAddingNew(false);
                    setNewTemplate({
                      category: selectedCategory,
                      value: '',
                      label: ''
                    });
                    setError(null);
                  }}
                  className="btn btn-secondary"
                >
                  Atšaukti
                </button>
              </div>
            </div>
          )}

          <div className="templates-list">
            {filteredTemplates.length === 0 ? (
              <div className="no-templates">
                Šioje kategorijoje nėra šablonų
              </div>
            ) : (
              <div className="templates-grid">
                {filteredTemplates.map((template) => (
                  <div key={template.id} className={`template-card ${editingTemplate?.id === template.id ? 'editing' : ''}`}>
                    {editingTemplate?.id === template.id ? (
                      // Edit mode
                      <div className="template-edit-form">
                        <div className="edit-field">
                          <label>Reikšmė:</label>
                          <input
                            type="text"
                            value={editForm.value}
                            onChange={(e) => setEditForm(prev => ({ ...prev, value: e.target.value }))}
                            placeholder="Reikšmė"
                          />
                        </div>
                        <div className="edit-actions">
                          <button onClick={handleSaveEdit} className="btn btn-success btn-sm">
                            ✓ Išsaugoti
                          </button>
                          <button onClick={handleCancelEdit} className="btn btn-secondary btn-sm">
                            ✕ Atšaukti
                          </button>
                        </div>
                      </div>
                    ) : (
                      // View mode
                      <>
                        {/* Template Image - Only show for company_activity category */}
                        {selectedCategory === 'company_activity' && (
                          <div className="template-image-section">
                            {template.image_url ? (
                              <div className="template-image-preview">
                                <img 
                                  src={`${window.location.origin}${template.image_url}`} 
                                  alt={template.value}
                                  onError={(e) => {
                                    console.log('Image load error:', template.image_url);
                                    (e.target as HTMLImageElement).style.display = 'none';
                                  }}
                                />
                                <button 
                                  onClick={() => handleDeleteImage(template.id)}
                                  className="btn btn-danger btn-xs image-delete-btn"
                                  title="Ištrinti nuotrauką"
                                >
                                  ✕
                                </button>
                              </div>
                            ) : (
                              <div className="template-image-placeholder">
                                <span>📷</span>
                              </div>
                            )}
                            <label className="image-upload-btn">
                              <input
                                type="file"
                                accept="image/*"
                                onChange={(e) => {
                                  const file = e.target.files?.[0];
                                  if (file) {
                                    handleImageUpload(template.id, file);
                                  }
                                }}
                                style={{ display: 'none' }}
                                disabled={uploadingImage === template.id}
                              />
                              {uploadingImage === template.id ? '⏳' : '📤'} {template.image_url ? 'Keisti' : 'Įkelti'}
                            </label>
                          </div>
                        )}
                        <div className="template-info">
                          <div className="template-label">{template.label || template.value}</div>
                          <div className="template-value">Reikšmė: {template.value}</div>
                          <div className="template-date">
                            Sukurta: {new Date(template.created_at).toLocaleDateString('lt-LT')}
                          </div>
                        </div>
                        <div className="template-actions">
                          <button
                            onClick={() => handleStartEdit(template)}
                            className="btn btn-primary btn-sm"
                          >
                            ✏️ Redaguoti
                          </button>
                          <button
                            onClick={() => handleDeleteTemplate(template.id)}
                            className="btn btn-danger btn-sm"
                          >
                            🗑️ Ištrinti
                          </button>
                        </div>
                      </>
                    )}
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default AdminCVTemplates;
